import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Info, CheckCircle, AlertTriangle } from 'lucide-react';

export default function IngestionModeModal({ isOpen, onClose, onSelectMode }) {
  const [selectedMode, setSelectedMode] = useState('dev');

  const handleConfirm = () => {
    onSelectMode(selectedMode);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gray-900 border-gray-800" data-testid="ingestion-mode-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">Select Ingestion Environment</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Alert className="bg-blue-950/50 border-blue-800">
            <Info className="h-4 w-4 text-blue-400" />
            <AlertDescription className="text-gray-300">
              Choose where to process this manuscript. This setting is temporary while production ingestion stabilizes.
            </AlertDescription>
          </Alert>

          {/* Dev Option (Recommended) */}
          <div 
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedMode === 'dev' 
                ? 'border-green-500 bg-green-950/30' 
                : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
            }`}
            onClick={() => setSelectedMode('dev')}
            data-testid="dev-option"
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                checked={selectedMode === 'dev'}
                onChange={() => setSelectedMode('dev')}
                className="mt-1 accent-green-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg text-white">Development Ingestion</h3>
                  <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">RECOMMENDED</span>
                </div>
                <p className="text-sm text-gray-400 mt-2">
                  Process manuscript in stable development environment. Your book will be reviewed and published by the NewAudia team.
                </p>
                <div className="mt-3 space-y-1 text-sm">
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    <span>Reliable and proven</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    <span>No platform timeouts</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    <span>Published after review</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Prod Option (Experimental) */}
          <div 
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedMode === 'prod' 
                ? 'border-yellow-500 bg-yellow-950/30' 
                : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
            }`}
            onClick={() => setSelectedMode('prod')}
            data-testid="prod-option"
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                checked={selectedMode === 'prod'}
                onChange={() => setSelectedMode('prod')}
                className="mt-1 accent-yellow-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg text-white">Production Ingestion</h3>
                  <span className="text-xs bg-yellow-600 text-white px-2 py-1 rounded">EXPERIMENTAL</span>
                </div>
                <p className="text-sm text-gray-400 mt-2">
                  Process manuscript directly in production environment. May encounter platform stability issues.
                </p>
                <div className="mt-3 space-y-1 text-sm text-yellow-400">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    <span>May experience timeouts or failures</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    <span>Use only for testing or when specifically needed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm} 
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-btn"
            >
              Continue with {selectedMode === 'dev' ? 'Development' : 'Production'} Ingestion
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
